import _ from 'lodash';
import { useMemo } from 'react';
import { useAppSelector } from '../../../app/hooks';
import {
  BlueberryFeatures,
  CubeDimensionEnum,
  FeatureEnum,
} from '../../../services/data/impl/cube/cubeSlice';
import {
  findHighestModelVersion,
  isBlueberryFeature,
  isBlueberryFlowerFeature,
} from '../../../services/data/impl/cube/cubeUtils';
import { ColumnConversion } from '../../commonUtils';
import useCurrServicesAndMeasures from '../../customerSummary/hooks/useCurrServicesAndMeasures';
import {
  selectColumnsExistInDbData,
  selectScanDataTableExists,
} from '../../duckDB/duckDBSlice';
import { DuckQueryReturnListType } from '../../duckDB/hooks/useDuckQueryScanData';
import {
  selectCurrFeature,
  selectCurrPointId,
  selectHoverPointId,
} from '../../select/selectSlice';
import { ScanData, ScanDataEnum } from '../scanDataDefinitions';
import { useBlueberryFlowerFeatures } from './useBlueberryFlowerFeatures';
import { OperatorEnum, useScanDataQuery } from './useScanDataQuery';

export enum BlueberryFilterEnum {
  point_id = ScanDataEnum.point_id,
}

export interface FilterBlueberryOptions {
  activeFilters?: BlueberryFilterEnum[];
  isMapHoverData?: boolean;
}

export interface BlueberryDataPerClass {
  [ScanDataEnum.raw_count]: number | null;
  [ScanDataEnum.count_per_meter]: number | null;
  [ScanDataEnum.estimated_count]: number | null;
  [ScanDataEnum.detection_area]: number | null;
  [ScanDataEnum.detection_length]: number | null;
}

// This structure will be used to group
// metrics by feature
export interface BlueberryData
  extends Omit<ScanData, keyof BlueberryDataPerClass> {
  [FeatureEnum.blueberry_Blue]?: BlueberryDataPerClass;
  [FeatureEnum.blueberry_Green]?: BlueberryDataPerClass;
  [FeatureEnum.blueberry_Purple]?: BlueberryDataPerClass;
  [FeatureEnum.blueberry_Yellow]?: BlueberryDataPerClass;
  [FeatureEnum.flower]?: BlueberryDataPerClass;
  [FeatureEnum.flower_closed]?: BlueberryDataPerClass;
  [FeatureEnum.flower_open]?: BlueberryDataPerClass;
  [FeatureEnum.flower_petalfall]?: BlueberryDataPerClass;
}

const BlueberryMeasureCols = [
  ScanDataEnum.raw_count,
  ScanDataEnum.count_per_meter,
  ScanDataEnum.estimated_count,
  ScanDataEnum.detection_area,
  ScanDataEnum.detection_length,
];

/**
 * Constructs a SQL alias expression with indexing for a given column, based on specified feature and conversion info.
 *
 * @param feature - The feature associated with the column.
 * @param index - The index number for the column.
 * @param conversionInfo - The conversion information for the column.
 * @returns A formatted string for use in a SQL query.
 * Example Output: 'count_per_meter[1] / 3.28084 as blueberry_Blue_count_per_meter'
 */
export function blueberryConversionWithIndex(
  feature: FeatureEnum,
  index: number,
  conversionInfo: ColumnConversion
): string {
  const indexedValue = conversionInfo.converted.replace(
    conversionInfo.original,
    `${conversionInfo.original}[${index}]`
  );
  return `${indexedValue} as ${feature}_${conversionInfo.original}`;
}

/**
 * Description
 * @param {BlueberryFilterEnum} activeFilters
 * @param {boolean} isMapHoverData
 * @returns {DuckQueryReturnListType<BlueberryData>}
 * This query is a special case because blueberries is multiclass model
 * And we need to display values for this classes at once
 */
export const useBlueberryQuery = ({
  activeFilters,
  isMapHoverData = false,
}: FilterBlueberryOptions): DuckQueryReturnListType<BlueberryData> => {
  const currFeature = useAppSelector(selectCurrFeature);
  const currPointId = useAppSelector(selectCurrPointId);
  const currHoverPointId = useAppSelector(selectHoverPointId);
  const pointId = isMapHoverData ? currHoverPointId : currPointId;
  const scanDataTableExists = useAppSelector(selectScanDataTableExists);
  const { servicesAndMeasuresWithFeatureFilter } = useCurrServicesAndMeasures();
  const columnsExistInDbData = useAppSelector(selectColumnsExistInDbData);

  const flowerFeatures = useBlueberryFlowerFeatures();

  const isBlueberry = useMemo(
    () =>
      isBlueberryFeature(currFeature) || isBlueberryFlowerFeature(currFeature),
    [currFeature]
  );

  // in blueberry case we need only model version and model name
  // model version will be always synced between classes
  const latestService = findHighestModelVersion(
    servicesAndMeasuresWithFeatureFilter.availableServicesRaw,
    [...BlueberryFeatures, ...flowerFeatures]
  );

  const [cols, filters] = useMemo(() => {
    // filter out columns that non existing in db
    const cols = columnsExistInDbData?.data
      ? Object.entries(columnsExistInDbData.data)
          .filter(([, exists]) => exists)
          .map(([col]) => col as ScanDataEnum)
      : [];
    const filters = [];

    if (latestService != null) {
      filters.push(
        {
          col: ScanDataEnum.model_name,
          op: OperatorEnum.eq,
          value: latestService[CubeDimensionEnum.modelName],
        },
        {
          col: ScanDataEnum.model_version,
          op: OperatorEnum.eq,
          value: latestService[CubeDimensionEnum.modelVersion],
        },
        {
          col: ScanDataEnum.class_name,
          op: OperatorEnum.in,
          value: [...BlueberryFeatures, ...flowerFeatures],
        }
      );
    }

    if (activeFilters?.includes(BlueberryFilterEnum.point_id) && pointId) {
      filters.push({
        col: ScanDataEnum.point_id,
        op: OperatorEnum.eq,
        value: pointId,
      });
    }

    return [cols, filters];
  }, [
    latestService,
    activeFilters,
    pointId,
    columnsExistInDbData?.data,
    flowerFeatures,
  ]);
  // at first this query order every row by class name
  // then it makes grouping by point
  // and merge rows that has counts into single row (array agg)
  const blueberryQueryResult = useScanDataQuery<ScanData>({
    cols,
    filters,
    skip:
      !latestService ||
      servicesAndMeasuresWithFeatureFilter.availableServicesRaw.length === 0 ||
      !isBlueberry ||
      !scanDataTableExists ||
      (activeFilters?.includes(BlueberryFilterEnum.point_id) && !pointId),
    disableClassFilter: true,
    useUnitsTransformSubstringMatching: true,
  });

  // group columns with counts per class into key-object where key is a class name
  const postProcessedResult = useMemo(() => {
    if (!blueberryQueryResult.data) {
      return null;
    }
    const groupedData = _.groupBy(
      blueberryQueryResult.data,
      ScanDataEnum.point_id
    );
    const pointsWithFeatureBasedData = Object.keys(groupedData).map(
      (pointId) => {
        const pointRelatedData = groupedData[pointId];
        const pointBase = _.omit(pointRelatedData[0], BlueberryMeasureCols);
        const classRelatedData = Object.fromEntries(
          Object.entries(
            _.groupBy(pointRelatedData, ScanDataEnum.class_name)
          ).map(([key, value]) => [
            key,
            _.pick(value[0], BlueberryMeasureCols) as BlueberryDataPerClass,
          ])
        ) as {
          [FeatureEnum.blueberry_Blue]: BlueberryDataPerClass;
          [FeatureEnum.blueberry_Green]: BlueberryDataPerClass;
          [FeatureEnum.blueberry_Purple]: BlueberryDataPerClass;
          [FeatureEnum.blueberry_Yellow]: BlueberryDataPerClass;
          [FeatureEnum.flower]: BlueberryDataPerClass;
          [FeatureEnum.flower_closed]: BlueberryDataPerClass;
          [FeatureEnum.flower_open]: BlueberryDataPerClass;
          [FeatureEnum.flower_petalfall]: BlueberryDataPerClass;
        };

        return {
          ...pointBase,
          ...classRelatedData,
        } as BlueberryData;
      }
    );

    return pointsWithFeatureBasedData;
  }, [blueberryQueryResult.data]);

  return {
    ...blueberryQueryResult,
    data: postProcessedResult,
  };
};
